import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Route, MapPin, ArrowRight, Download, CheckCircle, XCircle, AlertTriangle, Trash2, RotateCcw, Lock,
} from 'lucide-react';
import {
  ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, Field, Dialog, Tabs,
  PersonAvatar, StatusPill, money, num, plural, fmtDate, fmtDayMonth,
} from '../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW,
  RefreshButton, DateRange, CHART, codeLabel,
} from '../Financials/financeKit';

// ─── Constants ────────────────────────────────────────────────────────────────
// Trip purposes. `label` is what the page shows; `csv` is the exact text the CSV export has always
// written, kept as it was so logs exported before and after this screen changed still line up.
const PURPOSES = {
  work_travel:      { label: 'Work travel',        csv: 'Work Travel' },
  client_transport: { label: 'Client transport',   csv: 'Client Transport' },
  between_clients:  { label: 'Between clients',    csv: 'Between Clients' },
  training:         { label: 'Training / meeting', csv: 'Training / Meeting' },
  other:            { label: 'Other',              csv: 'Other' },
};

// A purpose code outside the list (auto-calculated travel is stored as 'travel') is shown in words
// instead of being folded into 'Other'.
const purposeLabel = (code) => PURPOSES[code]?.label || codeLabel(code) || 'Other';

// Status words written by the CSV export (unchanged). On screen a status is a StatusPill.
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

const VIEWS = [
  { id: 'trips', label: 'All trips' },
  { id: 'workers', label: 'By worker' },
  { id: 'chart', label: 'Chart' },
];

const APPROVAL_RULE = "Approving a trip here pre-approves it. Its KM is final once the shift's timesheet is approved.";
const EMPTY_HINT = 'Trips appear here when workers log them in the mobile app, or when travel is calculated on the Timesheets page.';

// Filters: label above the control. On phones two filters share a row; from `sm` each has its width.
const FILTER = 'min-w-[9.5rem] flex-1 sm:min-w-0 sm:flex-none';
const LABEL = 'mb-1 block text-[12px] font-medium text-slate-500';
const SELECT = 'input-field !py-2 text-sm';

// Icon-only row actions: neutral until hovered (colour lives in the Status column), 36px targets.
const ICON_BTN = 'inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors duration-150 disabled:opacity-40';

// Chart: distance in the primary accent with a flat light fill, the trip count in a neutral behind it.
const KM_COLOUR = '#4f46e5';     // primary-600
const TRIPS_COLOUR = '#cbd5e1';  // slate-300

// The per-km rate is stored to four decimal places, so it is shown as stored ('$0.85', '$0.925')
// rather than rounded to the cent. Display only: no amount is worked out from this text.
const rateLabel = (r) => {
  const v = Number(r) || 0;
  const places = (String(v).split('.')[1] || '').length;
  return `$${num(v, Math.min(4, Math.max(2, places)))}`;
};

const tripSummary = (t) => [
  t.staff_name,
  t.shift_date ? fmtDate(t.shift_date) : null,
  `${num(parseFloat(t.km || 0), 2)} km`,
].filter(Boolean).join(' · ');

// ─── Purpose badge ────────────────────────────────────────────────────────────
function PurposeBadge({ purpose }) {
  return <span className="badge-gray whitespace-nowrap">{purposeLabel(purpose)}</span>;
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const rows = [...payload].sort((a, b) => (a.dataKey === 'KM' ? -1 : 1) - (b.dataKey === 'KM' ? -1 : 1));
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-card-deep">
      <p className="mb-1.5 font-semibold text-slate-700">{label}</p>
      {rows.map((p) => (
        <p key={p.dataKey} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-sm" style={{ background: p.dataKey === 'KM' ? KM_COLOUR : TRIPS_COLOUR }} aria-hidden="true" />
            {p.name}
          </span>
          <span className="font-semibold tabular-nums text-slate-900">
            {p.dataKey === 'KM' ? `${num(p.value, 2)} km` : num(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Mileage() {
  const confirm = useConfirm();
  const today    = format(new Date(), 'yyyy-MM-dd');
  const monthAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [from, setFrom]         = useState(monthAgo);
  const [to, setTo]             = useState(today);
  const [staffId, setStaffId]   = useState('');
  const [clientId, setClientId] = useState('');
  const [statusF, setStatusF]   = useState('all');
  const [purposeF, setPurposeF] = useState('all');
  const [tab, setTab]           = useState('trips');  // 'trips' | 'workers' | 'chart'
  const [data, setData]         = useState(null);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Load participants (clients) for the filter dropdown
  useEffect(() => {
    api.get('/clients')
      .then(r => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || [])))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams({ from, to });
    if (staffId)            p.set('staff_id', staffId);
    if (clientId)           p.set('client_id', clientId);
    if (statusF !== 'all')  p.set('status', statusF);
    if (purposeF !== 'all') p.set('purpose', purposeF);
    api.get(`/timesheets/trips/all?${p}`)
      .then(r => setData(r.data))
      .catch((e) => {
        // A failed load says so, with a retry. It must never read as an empty log.
        const msg = e?.response?.data?.message;
        setError(msg ? `We couldn't load the trips (${msg}).` : "We couldn't load the trips. Check your connection, then try again.");
        toast.error('Failed to load trips');
      })
      .finally(() => setLoading(false));
  }, [from, to, staffId, clientId, statusF, purposeF]);

  useEffect(() => { load(); }, [load]);

  // ── Trip actions (admin) ──
  // Approve, reject, reset and delete call the same endpoints with the same bodies as before. Only
  // the questions in front of reject and delete moved from browser prompts into the app's dialogs.
  const [actioningId, setActioningId] = useState(null);
  const runAction = async (tripId, action, body) => {
    setActioningId(tripId);
    try {
      if (action === 'delete') {
        await api.delete(`/timesheets/trips/${tripId}`);
      } else {
        await api.post(`/timesheets/trips/${tripId}/${action}`, body || {});
      }
      toast.success({
        approve: 'Trip approved',
        reject:  'Trip rejected',
        reset:   'Trip reset to pending',
        delete:  'Trip deleted',
      }[action]);
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
      return false;
    } finally {
      setActioningId(null);
    }
  };

  const deleteTrip = async (tripId) => {
    const ok = await confirm('This removes the trip and its kilometres from the log. It cannot be undone.', {
      title: 'Delete this trip?', confirmLabel: 'Delete trip', danger: true,
    });
    if (ok) runAction(tripId, 'delete');
  };

  // Reject asks for an optional reason. Cancel closes the dialog and sends nothing; the reason is
  // sent exactly as typed ('' when left blank).
  const [rejecting, setRejecting]       = useState(null);   // the trip being rejected
  const [rejectReason, setRejectReason] = useState('');
  const openReject = (trip) => { setRejectReason(''); setRejecting(trip); };
  // Stable, so the dialog does not re-run its focus handling on every keystroke.
  const closeReject = useCallback(() => setRejecting(null), []);
  const submitReject = async () => {
    if (!rejecting) return;
    const done = await runAction(rejecting.id, 'reject', { reason: rejectReason });
    if (done) setRejecting(null);
  };
  const rejectBusy = !!rejecting && actioningId === rejecting.id;

  // CSV Export — columns and values unchanged.
  const exportCSV = () => {
    if (!data?.trips?.length) return;
    const RATE = data.mileageRate || 0.85;
    const headers = ['Date', 'Worker', 'Client', 'From', 'To', 'KM', 'Reimbursement ($)', 'Purpose', 'Status', 'Notes'];
    const rows = data.trips.map(t => [
      t.shift_date ? format(parseISO(t.shift_date), 'dd/MM/yyyy') : '',
      t.staff_name || '',
      t.client_names || '',
      t.from_address || '',
      t.to_address || '',
      parseFloat(t.km || 0).toFixed(2),
      (parseFloat(t.km || 0) * RATE).toFixed(2),
      PURPOSES[t.purpose]?.csv || t.purpose || '',
      STATUS_LABEL[t.status] || t.status || 'Pending',
      t.notes || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `mileage_${from}_to_${to}.csv`; a.click();
  };

  const chartData = useMemo(() =>
    (data?.chartData || []).map(d => ({
      date: fmtDayMonth(d.date),
      KM: parseFloat(d.km),
      Trips: d.trips,
    })),
  [data]);

  const byPurpose = useMemo(() => {
    const m = {};
    for (const t of data?.trips || []) {
      const p = t.purpose || 'work_travel';
      if (!m[p]) m[p] = { purpose: p, km: 0, trips: 0 };
      m[p].km += parseFloat(t.km || 0);
      m[p].trips++;
    }
    return Object.values(m).sort((a, b) => b.km - a.km);
  }, [data]);

  const trips        = data?.trips || [];
  const tripCount    = trips.length;
  const pendingCount = trips.filter(t => (t.status || 'pending') === 'pending').length;
  const rate         = data?.mileageRate || 0.85;
  const ready        = !!data && !error;   // figures on screen belong to the current filters

  // Loading, failed and empty look different: a skeleton, an error with a retry, then a hint.
  const stateFor = (hasRows, skeleton) => {
    if (loading) return skeleton;
    if (error) return <ErrorState message={error} onRetry={load} />;
    if (!hasRows) return <EmptyState icon={Route} title="No trips in this period" hint={EMPTY_HINT} />;
    return null;
  };

  const legend = (
    <div className="flex items-center gap-4 text-xs text-slate-600">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: KM_COLOUR }} aria-hidden="true" /> Distance (km)
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: TRIPS_COLOUR }} aria-hidden="true" /> Trips
      </span>
    </div>
  );

  return (
    <div className={PAGE_WIDE}>

      {/* ── Filters and actions ── */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="mileage" maxTo={today} />
          <div className={FILTER}>
            <label htmlFor="mileage-worker" className={LABEL}>Worker</label>
            <select id="mileage-worker" value={staffId} onChange={e => setStaffId(e.target.value)}
              className={clsx(SELECT, 'sm:!w-40')}>
              <option value="">All workers</option>
              {data?.workersList?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div className={FILTER}>
            <label htmlFor="mileage-participant" className={LABEL}>Participant</label>
            <select id="mileage-participant" value={clientId} onChange={e => setClientId(e.target.value)}
              className={clsx(SELECT, 'sm:!w-44')}>
              <option value="">All participants</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed'}
                </option>
              ))}
            </select>
          </div>
          <div className={FILTER}>
            <label htmlFor="mileage-status" className={LABEL}>Status</label>
            <select id="mileage-status" value={statusF} onChange={e => setStatusF(e.target.value)}
              className={clsx(SELECT, 'sm:!w-36')}>
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className={FILTER}>
            <label htmlFor="mileage-purpose" className={LABEL}>Purpose</label>
            <select id="mileage-purpose" value={purposeF} onChange={e => setPurposeF(e.target.value)}
              className={clsx(SELECT, 'sm:!w-44')}>
              <option value="all">All purposes</option>
              {Object.entries(PURPOSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={exportCSV} disabled={loading || !!error || !tripCount}
              className="btn-secondary btn-sm h-9">
              <Download size={14} aria-hidden="true" /> Export CSV
            </button>
            <RefreshButton onClick={load} loading={loading} />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-500">{APPROVAL_RULE}</p>
          {ready && pendingCount > 0 && (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
              <AlertTriangle size={12} aria-hidden="true" /> {plural(pendingCount, 'trip')} pending timesheet approval
            </span>
          )}
        </div>
      </div>

      {/* ── Figures ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile accent="slate" label="Total km"
          value={ready ? `${num(data.totalKm || 0, 2)} km` : '—'}
          sub={ready ? plural(tripCount, 'trip') : undefined} />
        <KpiTile accent="emerald" label="Approved km"
          value={ready ? `${num(data.approvedKm || 0, 2)} km` : '—'}
          sub={ready ? `${num(data.pendingKm || 0, 2)} km pending` : undefined} />
        <KpiTile accent="slate" label="Reimbursement"
          value={ready ? money(data.totalAmount || 0) : '—'}
          sub={ready ? `@ ${rateLabel(rate)}/km on trips not rejected (commutes excluded) · ATO log` : undefined} />
        <KpiTile accent="slate" label="Workers"
          value={ready ? num(data.workers || 0) : '—'}
          sub={ready ? `${num(data.avgKm || 0, 2)} km average per trip` : undefined} />
      </div>

      <div className="space-y-3">
        <Tabs tabs={VIEWS} value={tab} onChange={setTab} variant="segmented" ariaLabel="Mileage views" />

        {/* ── Trip log ── */}
        {tab === 'trips' && (
          <SectionCard title="Trip log"
            actions={ready && !loading ? <span className="text-xs tabular-nums text-slate-500">{plural(tripCount, 'trip')}</span> : null}>
            {stateFor(tripCount > 0, <TableSkeleton rows={6} cols={8} />) || (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <caption className="sr-only">Trip log</caption>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Date</th>
                      <th scope="col" className={TH}>Worker</th>
                      <th scope="col" className={TH}>Client</th>
                      <th scope="col" className={TH}>Route</th>
                      <th scope="col" className={TH_NUM}>KM</th>
                      <th scope="col" className={TH_NUM}>Amount</th>
                      <th scope="col" className={TH}>Purpose</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={TH_NUM}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map((t) => {
                      const km   = parseFloat(t.km || 0);
                      const amt  = km * (data.mileageRate || 0.85);
                      const stat = t.status || 'pending';
                      const busy = actioningId === t.id;
                      return (
                        <tr key={t.id} className={TR}>
                          <td className={clsx(TD, 'whitespace-nowrap')}>
                            {t.shift_date ? fmtDate(t.shift_date) : '—'}
                          </td>
                          <td className={TD}>
                            <div className="flex items-center gap-2">
                              <PersonAvatar id={t.staff_id} name={t.staff_name} size="xs" />
                              <span className="whitespace-nowrap font-medium text-slate-900">{t.staff_name}</span>
                            </div>
                          </td>
                          <td className={TD}>
                            <span className="block max-w-[160px] truncate text-slate-600" title={t.client_names || undefined}>
                              {t.client_names || '—'}
                            </span>
                          </td>
                          {/* From and to stack on two lines. Side by side (and a 280px note under
                              them) the route alone was ~310px, which pushed the table past its card
                              at 1600px and cut the reject button off at the right edge. */}
                          <td className={TD}>
                            <div className="flex items-center gap-1.5 text-xs text-slate-600">
                              <MapPin size={11} className="shrink-0 text-slate-400" aria-hidden="true" />
                              <span className="max-w-[150px] truncate" title={t.from_address || undefined}>{t.from_address || '—'}</span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600">
                              <ArrowRight size={11} className="shrink-0 text-slate-400" aria-hidden="true" />
                              <span className="sr-only">to</span>
                              <span className="max-w-[150px] truncate" title={t.to_address || undefined}>{t.to_address || '—'}</span>
                            </div>
                            {t.notes && (
                              <p className="mt-0.5 max-w-[170px] truncate text-[11px] text-slate-500" title={t.notes}>{t.notes}</p>
                            )}
                          </td>
                          <td className={TD_NUM}>{num(km, 2)} km</td>
                          <td className={TD_NUM}>
                            <span className={stat === 'approved' ? 'text-slate-900' : 'text-slate-500'}>{money(amt)}</span>
                          </td>
                          <td className={TD}><PurposeBadge purpose={t.purpose} /></td>
                          <td className={TD}>
                            <StatusPill status={stat} />
                            {t.ts_status === 'approved' && t.km_approved === false && stat === 'pending' && (
                              <p className="mt-1 whitespace-nowrap text-[11px] text-amber-700" title="The timesheet was approved with its KM held — not paid or charged">KM held at approval</p>
                            )}
                            {t.rejection_reason && (
                              <p className="mt-1 max-w-[160px] truncate text-[11px] text-red-700" title={t.rejection_reason}>
                                {t.rejection_reason}
                              </p>
                            )}
                          </td>
                          <td className={TD}>
                            <div className="flex items-center justify-end gap-1">
                              {t.ts_status === 'approved' ? (
                                // The timesheet is approved: this KM is paid and charged. Nothing here
                                // may change it — unapprove the timesheet first.
                                <span title="The timesheet is approved, so this KM is locked. Unapprove the timesheet to change it."
                                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                                  <Lock size={12} aria-hidden="true" /> Locked
                                </span>
                              ) : stat === 'rejected' ? (
                                // Rejected: the only move is to take the rejection back.
                                <button type="button" onClick={() => runAction(t.id, 'reset')} disabled={busy}
                                  aria-label="Reset to pending" title="Reset to pending"
                                  className={clsx(ICON_BTN, 'hover:bg-slate-100 hover:text-slate-800')}>
                                  <RotateCcw size={16} aria-hidden="true" />
                                </button>
                              ) : (
                                <>
                                  {stat !== 'approved' && (
                                    <button type="button" onClick={() => runAction(t.id, 'approve')} disabled={busy}
                                      aria-label="Approve trip" title="Approve trip"
                                      className={clsx(ICON_BTN, 'hover:bg-emerald-50 hover:text-emerald-700')}>
                                      <CheckCircle size={16} aria-hidden="true" />
                                    </button>
                                  )}
                                  <button type="button" onClick={() => openReject(t)} disabled={busy}
                                    aria-label="Reject trip" title="Reject trip"
                                    className={clsx(ICON_BTN, 'hover:bg-red-50 hover:text-red-700')}>
                                    <XCircle size={16} aria-hidden="true" />
                                  </button>
                                  {stat !== 'pending' && (
                                    <button type="button" onClick={() => runAction(t.id, 'reset')} disabled={busy}
                                      aria-label="Reset to pending" title="Reset to pending"
                                      className={clsx(ICON_BTN, 'hover:bg-slate-100 hover:text-slate-800')}>
                                      <RotateCcw size={16} aria-hidden="true" />
                                    </button>
                                  )}
                                  <button type="button" onClick={() => deleteTrip(t.id)} disabled={busy}
                                    aria-label="Delete trip" title="Delete trip"
                                    className={clsx(ICON_BTN, 'hover:bg-red-50 hover:text-red-700')}>
                                    <Trash2 size={16} aria-hidden="true" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td colSpan={4} className={TD}>Total — {plural(tripCount, 'trip')}</td>
                      <td className={TD_NUM}>{num(data.totalKm || 0, 2)} km</td>
                      <td className={TD_NUM}>
                        {money(data.totalAmount || 0)}
                        <span className="block text-[11px] font-normal text-slate-500">not rejected</span>
                      </td>
                      <td colSpan={3} className={TD} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── By worker ── */}
        {tab === 'workers' && (
          <SectionCard title="Reimbursement by worker"
            actions={ready && !loading ? <span className="text-xs text-slate-500">Km not rejected (commutes excluded) × {rateLabel(rate)}/km</span> : null}>
            {stateFor((data?.workerBreakdown?.length || 0) > 0, <TableSkeleton rows={5} cols={7} />) || (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <caption className="sr-only">Reimbursement by worker</caption>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Worker</th>
                      <th scope="col" className={TH_NUM}>Trips</th>
                      <th scope="col" className={TH_NUM}>Total km</th>
                      <th scope="col" className={TH_NUM}>Approved km</th>
                      <th scope="col" className={TH_NUM}>Pending km</th>
                      <th scope="col" className={TH_NUM}>Reimbursement</th>
                      <th scope="col" className={TH}>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.workerBreakdown.map((w) => {
                      const pct = w.km > 0 ? (w.approved_km / w.km) * 100 : 0;
                      return (
                        <tr key={w.staff_id} className={TR}>
                          <td className={TD}>
                            <div className="flex items-center gap-2.5">
                              <PersonAvatar id={w.staff_id} name={w.staff_name} size="sm" />
                              <span className="whitespace-nowrap font-medium text-slate-900">{w.staff_name}</span>
                            </div>
                          </td>
                          <td className={TD_NUM}>{num(w.trips)}</td>
                          <td className={TD_NUM}>{num(w.km, 2)} km</td>
                          <td className={TD_NUM}>{num(w.approved_km, 2)} km</td>
                          <td className={TD_NUM}>{num(w.pending_km, 2)} km</td>
                          <td className={TD_NUM}>{money(w.amount)}</td>
                          <td className={clsx(TD, 'w-40')}>
                            <div className="flex items-center gap-2">
                              <div className="h-2 min-w-[3rem] flex-1 rounded-full bg-slate-100" aria-hidden="true">
                                <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                              <span className="whitespace-nowrap text-xs tabular-nums text-slate-500">{num(pct, 0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD}>Total</td>
                      <td className={TD_NUM}>{num(tripCount)}</td>
                      <td className={TD_NUM}>{num(data.totalKm || 0, 2)} km</td>
                      <td className={TD_NUM}>{num(data.approvedKm || 0, 2)} km</td>
                      <td className={TD_NUM}>{num(data.pendingKm || 0, 2)} km</td>
                      <td className={TD_NUM}>{money(data.totalAmount || 0)}</td>
                      <td className={TD} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── Chart ── */}
        {tab === 'chart' && (
          <div className="space-y-4">
            <SectionCard title="Kilometres per day" actions={ready && !loading && chartData.length > 0 ? legend : null}>
              {stateFor(chartData.length > 0, (
                <div className="p-5" aria-busy="true" aria-label="Loading"><div className="skeleton h-[220px] w-full" /></div>
              )) || (
                <div className="p-5" role="img" aria-label="Kilometres travelled and trips logged per day">
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="2 4" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: CHART.axis }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="km" tick={{ fontSize: 11, fill: CHART.axis }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="trips" orientation="right" tick={{ fontSize: 11, fill: CHART.axis }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTip />} />
                      <Bar yAxisId="trips" dataKey="Trips" name="Trips" fill={TRIPS_COLOUR} radius={[3, 3, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                      <Area yAxisId="km" type="monotone" dataKey="KM" name="Distance" stroke={KM_COLOUR} strokeWidth={2}
                        fill={KM_COLOUR} fillOpacity={0.08} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>

            {/* Purpose breakdown */}
            {ready && !loading && byPurpose.length > 0 && (
              <SectionCard title="Kilometres by purpose" bodyClass="space-y-3 p-5">
                {byPurpose.map((p) => {
                  const pct = (p.km / (data.totalKm || 1)) * 100;
                  const label = purposeLabel(p.purpose);
                  return (
                    <div key={p.purpose} className="flex items-center gap-3">
                      <div className="w-28 shrink-0 truncate text-xs font-medium text-slate-700 sm:w-36" title={label}>{label}</div>
                      <div className="h-2.5 min-w-0 flex-1 rounded-full bg-slate-100" aria-hidden="true">
                        <div className="h-2.5 rounded-full bg-primary-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <div className="w-36 shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-slate-900">
                        {num(p.km, 2)} km <span className="ml-1 text-slate-500">({num(pct, 0)}%)</span>
                      </div>
                    </div>
                  );
                })}
              </SectionCard>
            )}
          </div>
        )}
      </div>

      {/* ── Reject a trip ── */}
      <Dialog
        open={!!rejecting}
        onClose={closeReject}
        title="Reject this trip?"
        description={rejecting ? tripSummary(rejecting) : undefined}
        size="sm"
        footer={(
          <>
            <button type="button" onClick={closeReject} className="btn-secondary">Cancel</button>
            <button type="button" onClick={submitReject} disabled={rejectBusy} className="btn-danger">
              {rejectBusy ? 'Rejecting…' : 'Reject trip'}
            </button>
          </>
        )}
      >
        <Field label="Reason" hint="Optional. It is shown under the trip's status in this log.">
          {(id) => (
            <textarea id={id} rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              className="input-field resize-y" />
          )}
        </Field>
      </Dialog>
    </div>
  );
}
